import { token } from '@atlaskit/tokens';
import { css, keyframes, styled } from '@compiled/react';
import { createPlacementComponent } from '@post-office/placement-common/utils/create-placement-component';
import type { MessageProps } from '@post-office/placement-common/utils/message-renderer';
import { useDisableChoreographer, useInitialData } from '@post-office/placement-contexts/hooks';

import { MessageRenderer } from '../__generated__/client/render';
import type { renderer } from '../__generated__/client/render';
import { withProductStorePersonalUseCaseIntlProvider } from '../__generated__/intl-provider';
import type { ProductStorePersonalUseCaseInitialData } from '../api/types';
import { type ProductStorePersonalUseCasePlacement } from '../contracts/Placement';
import type { Fetcher } from '@atlassian/post-office-placement-data/types';
import { type OnPerformanceTrackingEvent } from '@post-office/placement-contexts/create-placement-component/hooks/usePerformanceTracking/types';
import {
	type ContextWithRoute,
	type FallbackRenderFacade,
} from '@post-office/placement-contexts/create-placement-component/types';
import { type ComponentType } from 'react';
import { type ErrorBoundaryPropsWithRender } from 'react-error-boundary';

const PLACEMENT_ID = 'product-store-personal-use-case';

export type Message = MessageProps<typeof renderer>;

type LoadingSkeletonProps = {
	width: string;
	height: string;
	borderRadius: string;
};

const shimmer = keyframes({
	'0%': {
		backgroundPosition: '-300px 0',
	},
	'100%': {
		backgroundPosition: '1000px 0',
	},
});

const LoadingSkeleton = styled.div<LoadingSkeletonProps>(({ width, borderRadius, height }) => ({
	animationDuration: '1.2s',
	animationFillMode: 'forwards',
	animationIterationCount: 'infinite',
	animationName: shimmer,
	animationTimingFunction: 'linear',
	backgroundColor: token('color.skeleton', 'grey'),
	backgroundImage: `linear-gradient( to right, ${token('color.skeleton', 'grey')} 10%, ${token('color.skeleton.subtle', 'grey')} 20%, ${token('color.skeleton', 'grey')} 30% )`,
	backgroundRepeat: 'no-repeat',
	borderRadius: borderRadius,
	height: height,
	width: width,
	marginRight: 0,
	marginTop: 0,
	marginBottom: 0,
	marginLeft: 0,
}));

const placeholderStyles = css({
	maxHeight: '400px',
	marginTop: '25px',
	padding: '54px 64px 54px 64px',
	overflow: 'hidden',
	border: `${token('border.width', '1px')} solid ${token('color.border', '#091E42')}`,
	display: 'flex',
	flex: '1 100%',
	borderRadius: token('radius.small', '3px'),
	justifyContent: 'space-between',
	alignItems: 'center',
});

const bannerTextContainer = css({
	alignSelf: 'center',
	display: 'flex',
	flexDirection: 'column',
	rowGap: '24px',
	flex: 1,
	paddingRight: '54px',
});

const imageContainer = css({
	display: 'none',
	'@media (min-width: 1000px)': {
		display: 'flex',
		flex: 1,
	},
});

export const ProductStorePersonalUseCasePlacementComponent: ComponentType<
	Partial<ErrorBoundaryPropsWithRender> & {
		context: ContextWithRoute;
		initialData: Promise<Response | undefined> | undefined;
		fetch?: Fetcher;
		onEvent?: OnPerformanceTrackingEvent;
	} & ProductStorePersonalUseCasePlacement &
		FallbackRenderFacade & {
			messages?: {
				[x: string]: string;
			};
		}
> = withProductStorePersonalUseCaseIntlProvider(
	createPlacementComponent<ProductStorePersonalUseCasePlacement>(
		PLACEMENT_ID,
		({ initialData, onLearnMore, onTryNow, onLoadComplete }) => {
			// Disabling this placement from being choreographed as it is not a pop-up
			useDisableChoreographer();

			const { data, isLoading, error } =
				useInitialData<ProductStorePersonalUseCaseInitialData>(initialData);

			if (isLoading) {
				return (
					<div css={placeholderStyles}>
						<div css={bannerTextContainer}>
							<LoadingSkeleton width="158px" height="32px" borderRadius="3px" />
							<LoadingSkeleton width="467px" height="32px" borderRadius="3px" />
							<LoadingSkeleton width="449px" height="36px" borderRadius="3px" />
							<LoadingSkeleton width="200px" height="32px" borderRadius="3px" />
						</div>
						<div css={imageContainer}>
							<LoadingSkeleton width="100%" height="290px" borderRadius="3px" />
						</div>
					</div>
				);
			}

			if (error) {
				onLoadComplete('');
				throw error;
			}

			onLoadComplete(data.messages[0]?.messageTemplateId || '');
			return data.messages.map((e: Message, i) => (
				<MessageRenderer key={i} {...e} onTryNow={onTryNow} onLearnMore={onLearnMore} />
			));
		},
	),
);

export default ProductStorePersonalUseCasePlacementComponent;
